import React, { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { DatePickerWithRange } from "@/components/ui/date-range/date-rangePicker";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import TableFilters from "@/components/ui/table/data-table-filter";
import { statusValues } from "@/lib/constants/sale_history";
import { useGetMembersListQuery } from "@/services/memberApi";
import { useGetIncomeCategoryQuery } from "@/services/incomeCategoryApi";
import { useGetAllExtensionsQuery } from "@/services/extensionApi";
import { useGetCountersQuery } from "@/services/counterApi";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";

interface SalesReportFiltersProps {
  dateRange?: {
    start_date?: string;
    end_date?: string;
  };
  appliedDateRange?: {
    start_date?: string;
    end_date?: string;
  };
  onDateRangeChange?: (dates: {
    start_date: Date | undefined;
    end_date: Date | undefined;
  }) => void;
  onResetFilters?: () => void;
  onApplyFilters?: () => void;
  filterData: Record<string, any>;
  setFilterData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  openFilter: boolean;
  setOpenFilter: React.Dispatch<React.SetStateAction<boolean>>;
  initialValue: any;
  showAdvancedFilters?: boolean;
}

export default function SalesReportFilters({
  dateRange,
  appliedDateRange,
  onDateRangeChange,
  onResetFilters,
  onApplyFilters,
  filterData,
  setFilterData,
  openFilter,
  setOpenFilter,
  initialValue,
  showAdvancedFilters = true
}: SalesReportFiltersProps) {
  const orgId = useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;

  // Data queries
  const { data: incomeCategoryData } = useGetIncomeCategoryQuery({
    org_id: orgId, 
    query: "sort_key=id&sort_order=desc&status=active"
  }, {
    skip: !orgId || orgId === 0
  });

  const { data: membersData } = useGetMembersListQuery({
    id: Number(orgId),
    query: "",
  });

  const { data: enabledPayments } = useGetAllExtensionsQuery({ 
    org_id: orgId, 
    query: `type=payment&is_active=true` 
  });

  const { data: counterList } = useGetCountersQuery(
    { query: `status=active` }
  );

  // Process options
  const incomeCategoryOptions = useMemo(() => {
    return Array.isArray(incomeCategoryData?.data)
      ? incomeCategoryData?.data.map((item) => ({
        ...item,
        id: item.id?.toString() || "",
        name: item.name,
      }))
      : [];
  }, [incomeCategoryData]);

  const enabledPaymentsOptions = useMemo(() => {
    return Array.isArray(enabledPayments?.data) 
      ? enabledPayments?.data.map((item) => ({
        value: item.id,
        label: item.name,
      })) 
      : [];
  }, [enabledPayments]);

  const countersOptions = useMemo(() => {
    return Array.isArray(counterList?.data) 
      ? counterList?.data.map((item) => ({
        value: item.id,
        label: item.name,
      })) 
      : [];
  }, [counterList]);

  function handleFilterChange(field: string, value: string | number | (string | number)[]) {
    setFilterData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  const filterDisplay = useMemo(() => [
    {
      type: "select",
      name: "status",
      label: "Status",
      options: statusValues.map((item) => ({
        id: item.value,
        name: item.label,
      })),
      function: (value: string) => handleFilterChange("status", value),
    },
    {
      type: "select",
      name: "member_id",
      label: "Members",
      options: membersData?.map((item) => ({
        id: item.value,
        name: item.label,
      })),
      function: (value: string) => handleFilterChange("member_id", value),
    },
    {
      type: "select",
      name: "type_ui",
      label: "Type",
      options: [
        { name: "Sales", id: "Sale" },
        { name: "Refund", id: "Refund" },
        // Unique UI value to distinguish selection while mapping to type=Sale
        { name: "Failed SRB Sales", id: "Sale_SRB" },
      ],
      function: (value: string, option?: { name?: string; id?: string }) => {
        setFilterData((prev) => {
          const isFailedSrb = option?.id === "Sale_SRB";
          const typeValue = value === "Refund" ? "Refund" : "Sale";
          const next = { ...prev } as Record<string, any>;
          next.type_ui = value; // keep UI selection distinct
          next.type = typeValue; // backend filter value
          if (isFailedSrb) {
            next.only_srb = true;
          } else {
            delete next.only_srb;
          }
          return next;
        });
      },
    },
    {
      type: "multiselect",
      name: "income_category_ids",
      label: "Income Category",
      options: incomeCategoryOptions?.map((item) => ({
        value: item.id,
        label: item.name,
      })),
      function: (value: (string | number)[]) => {
        // Keep as array - parent will handle converting to query params
        handleFilterChange("income_category_ids", value);
      },
    },
    {
      type: "multiselect",
      name: "payment_method_ids",
      label: "Payment Method",
      options: enabledPaymentsOptions,
      function: (value: (string | number)[]) => {
        // Keep as array - parent will handle converting to query params
        handleFilterChange("payment_method_ids", value);
      },
    },
    {
      type: "multiselect",
      name: "counter_ids",
      label: "Counter",
      options: countersOptions,
      function: (value: (string | number)[]) => {
        // Keep as array - parent will handle converting to query params
        handleFilterChange("counter_ids", value);
      },
    }
  ], [statusValues, membersData, incomeCategoryOptions, enabledPaymentsOptions, countersOptions]);

  const handleApplyFiltersClick = () => {
    onApplyFilters?.();
  };

  const resetFilters = () => {
    setFilterData({});
    onResetFilters?.();
  };

  return (
    <>
      {/* Main Filter Controls */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
              <div className="text-sm text-black flex items-center gap-1 lg:mb-0 h-8">
                <DatePickerWithRange
                  name={"Date Range"}
                  value={{
                    start_date: dateRange?.start_date ? new Date(dateRange.start_date) : undefined,
                    end_date: dateRange?.end_date ? new Date(dateRange.end_date) : undefined,
                  }}
                  onValueChange={(dates) => {
                    onDateRangeChange?.(dates);
                  }}
                  label={"Select date range"}
                  className="w-full"
                />
              </div>
              <Button
                variant={"default"}
                className="text-sm bg-primary text-black flex items-center gap-1 lg:mb-0 h-8 px-3"
                onClick={handleApplyFiltersClick}
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="gap-2 flex justify-center items-center">
                        <i className="fa fa-filter"></i>
                        Apply
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Apply Filters</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Button>
              <Button
                variant={"outline"}
                className="text-sm text-black flex items-center gap-1 lg:mb-0 h-8 px-2"
                onClick={resetFilters}
              >
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="gap-2 flex justify-center items-center">
                        <i className="fa-solid fa-arrows-rotate"></i>
                        Reset
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Reset Filter</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </Button>
              
              {showAdvancedFilters && (
                <button
                  className="border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center"
                  onClick={() => setOpenFilter(true)}
                >
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <i className="fa fa-filter"></i>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Advanced filters</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Advanced Filters Modal */}
      {showAdvancedFilters && (
        <TableFilters
          isOpen={openFilter}
          setOpen={setOpenFilter}
          initialValue={initialValue}
          filterData={filterData}
          setFilter={setFilterData}
          setSearchCriteria={(value: any) => {
            // When user clicks "Apply filters" in the modal, call parent's apply function
            if (typeof value === 'function') {
              setFilterData(value);
            } else {
              setFilterData(value);
            }
            handleApplyFiltersClick();
          }}
          filterDisplay={filterDisplay}
          setInputValue={() => {}} // Not used in this context
          onReset={resetFilters}
        />
      )}
    </>
  );
}
